import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PageLoader } from "@/components/common/loader";
import { Card } from "@/components/ui/card";
import { listNotices, type PortalNotice } from "@/features/portal/api";
import { formatPortalDate as fmt } from "@/features/portal/portal-format";
import { cn } from "@/lib/utils";

const STATUS_LABEL: Record<PortalNotice["status"], string> = {
  OPEN: "Open",
  IN_PROGRESS: "In progress",
  RESOLVED: "Resolved",
};

/** `/portal/notices` — IRS notices the firm has logged for this client, read-only. */
export function PortalNotices() {
  const [notices, setNotices] = useState<PortalNotice[] | null>(null);

  useEffect(() => {
    let active = true;
    listNotices()
      .then((list) => active && setNotices(list))
      .catch(() => {
        if (!active) return;
        setNotices((prev) => prev ?? []);
        toast.error("Couldn't load your notices. Please refresh.");
      });
    return () => {
      active = false;
    };
  }, []);

  if (notices === null) return <PageLoader />;

  return (
    <div className="mx-auto w-full max-w-3xl space-y-6 py-8">
      <h1 className="text-2xl font-semibold text-foreground">Notices</h1>
      {notices.length === 0 ? (
        <Card className="p-8 text-center">
          <p className="text-muted-foreground text-sm">
            You have no IRS notices on file. If you receive one, your tax team will log it here.
          </p>
        </Card>
      ) : (
        <Card className="gap-0 divide-y overflow-hidden p-0">
          {notices.map((n) => (
            <div key={n.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
              <div className="flex min-w-0 flex-col leading-tight">
                <span className="font-medium text-foreground">
                  {n.code}
                  {n.taxYear ? ` · ${n.taxYear}` : ""}
                </span>
                <span className="text-muted-foreground truncate text-sm">{n.title}</span>
                <span className="text-muted-foreground text-xs">
                  Received {fmt(n.receivedOn)}
                  {n.responseDeadline ? ` · respond by ${fmt(n.responseDeadline)}` : ""}
                </span>
              </div>
              <span
                className={cn(
                  "shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold",
                  n.status === "RESOLVED"
                    ? "bg-success-soft text-success"
                    : n.status === "IN_PROGRESS"
                      ? "bg-info-soft text-info"
                      : "bg-warning-soft text-warning",
                )}
              >
                {STATUS_LABEL[n.status]}
              </span>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}
